import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ClipboardCheck, Trophy, ArrowRight, Eye } from 'lucide-react';

const features = [
  {
    icon: Eye,
    title: 'Live Monitoring',
    description: 'See which players are training in real-time. No more guessing.',
  },
  {
    icon: ClipboardCheck,
    title: 'Custom Assignments',
    description: 'Create structured homework and track verified completion.',
  },
  {
    icon: Trophy,
    title: 'Team Rankings',
    description: 'Automated leaderboards that drive friendly competition.',
  },
  {
    icon: BarChart3,
    title: 'Progress Analytics',
    description: 'Individual and team-level insights on every touch and rep.',
  },
];

export function ClubPortalSection() {
  const navigate = useNavigate();

  return (
    <section id="clubs" className="py-12 md:py-24 bg-foreground text-background">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Content */}
          <div>
            <span className="inline-block px-3 py-1 bg-amber-400/20 text-amber-400 rounded-full text-xs font-medium mb-4">
              For Coaches & Clubs
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 leading-tight">
              Stop Guessing If Your Players Are Training at Home.
            </h2>
            <p className="text-background/70 text-sm md:text-base mb-8 leading-relaxed max-w-lg">
              FutWork gives coaches the dashboard to verify every touch, every rep, and every decision. Turn "homework" into measurable player development.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.title}
                    className="p-3 md:p-4 rounded-xl bg-background/5 border border-background/10 hover:bg-background/10 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-400/15 flex items-center justify-center mb-2">
                      <Icon className="h-4 w-4 text-amber-400" />
                    </div>
                    <h3 className="text-sm font-semibold mb-0.5">{feature.title}</h3>
                    <p className="text-xs text-background/50 leading-snug">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            <Button
              size="lg"
              onClick={() => navigate('/coach-auth')}
              className="bg-gradient-to-r from-amber-400 to-orange-500 text-foreground hover:from-amber-500 hover:to-orange-600 rounded-full px-6 h-12 md:h-14 text-sm md:text-base font-semibold shadow-lg shadow-amber-500/25 active:scale-[0.98]"
            >
              See the Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Right: Visual - App screenshot placeholder */}
          <div className="relative flex justify-center">
            <div className="relative w-full max-w-[352px] md:max-w-[440px]">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border border-background/10">
                <video
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source src="/videos/management.mp4" type="video/mp4" />
                </video>
                {/* Overlay badge */}
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md rounded-lg px-3 py-1.5 border border-white/10">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[11px] text-white font-medium">Live Dashboard</span>
                  </div>
                </div>
              </div>
              <div className="absolute -z-10 inset-0 bg-gradient-to-br from-amber-500/20 via-transparent to-orange-500/20 blur-3xl scale-150" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
